import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useRegisterMutation, useAnswerQuestionMutation } from '../../apis';
import { CheckBox, CheckBoxVariants, Input, InputVariants } from "../../UI";
import { LEGAL_VERSION } from "../../config/legal";
import './SignupForm.styles.css';
import { useInput } from "../../hooks/useInput";
import { takePendingVote } from "../../utils/pendingVote";
import { Spinner, SpinnerVariants } from "../../UI";
import isEmail from "validator/lib/isEmail";

export const SignUpForm: React.FC = () => {

  const {
    inputValue: firstNameValue,
    hasError: firstNameError,
    onInputChange: onFirstNameChange,
    onInputBlur: onFirstNameBlur,
  } = useInput((value) => (/^[\p{L}\p{M} '-]+$/u).test(value.trim()));
  const {
    inputValue: lastNameValue,
    hasError: lastNameError,
    onInputChange: onLastNameChange,
    onInputBlur: onLastNameBlur,
  } = useInput((value) => (/^[\p{L}\p{M} '-]+$/u).test(value.trim()));

  const {
    inputValue: emailValue,
    hasError: emailError,
    onInputChange: onEmailChange,
    onInputBlur: onEmailBlur,
  } = useInput(isEmail);
  const {
    inputValue: passwordValue,
    hasError: passwordError,
    onInputChange: onPasswordChange,
    onInputBlur: onPasswordBlur,
  } = useInput((value) => value.length >= 8);
  const {
    inputValue: confirmPasswordValue,
    hasError: confirmPasswordError,
    onInputChange: onConfirmPasswordChange,
    onInputBlur: onConfirmPasswordBlur,
  } = useInput((value) => value === passwordValue);

  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const [answerQuestion] = useAnswerQuestionMutation();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | false>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (firstNameError || lastNameError || emailError
      || passwordError || confirmPasswordError || !isChecked) {
      return;
    }

    if (passwordValue !== confirmPasswordValue) {
      return;
    }

    setServerError(false);

    try {
      await register({
        firstName: firstNameValue,
        lastName: lastNameValue,
        email: emailValue,
        password: passwordValue,
        acceptedTerms: isChecked,
        consentVersion: LEGAL_VERSION,
      }).unwrap();
      // Signup doesn't reload the page, so the app-root replay won't fire here —
      // lock in a vote-first tap inline before leaving the form.
      const pending = takePendingVote();
      if (pending) {
        // mode:'set' — idempotent lock-in, consistent with the app-root replay.
        answerQuestion({ ...pending, mode: 'set' }).unwrap().catch(() => { /* feed reflects truth later */ });
      }
      // New accounts land on the welcome quiz so they bank a few answers up
      // front — Soulmates needs shared answers to work, and a fresh account has
      // none. The quiz has its own Skip.
      navigate('/welcome');
    } catch (error: unknown) {
      const err = error as {
        data?: { message?: string };
        message?: string;
        status?: number | string;
      };

      // Make browser-side vs API-side failures distinguishable in Sentry.
      // RTK Query's error `status` is an HTTP number when the API responded,
      // or a string tag (e.g. FETCH_ERROR / TIMEOUT_ERROR) when the request
      // never got a usable response — the key signal for "the POST may be
      // failing client-side before it reaches the API".
      const status = err?.status;
      const outcome =
        typeof status === 'number'
          ? 'api_error'
          : status === 'FETCH_ERROR'
            ? 'network_error'
            : status === 'TIMEOUT_ERROR'
              ? 'timeout'
              : status === 'PARSING_ERROR'
                ? 'parsing_error'
                : 'unknown_error';

      Sentry.captureException(
        new Error(`Signup failed: ${outcome} (status=${String(status ?? 'none')})`),
        {
          tags: {
            area: 'auth',
            outcome,
            http_status: typeof status === 'number' ? status : String(status ?? 'none'),
          },
          // No email/password — only the error shape and API message.
          extra: { rtkStatus: status, apiMessage: err?.data?.message },
        }
      );

      setServerError(err?.data?.message || err?.message || 'Registration failed');
    }
  };

  const handleCheckBoxChange = (isChecked: boolean) => setIsChecked(isChecked);


  return (
    <>
      {isLoading && <Spinner variant={SpinnerVariants.CIRCLE} />}
      <div className="signup-form-wrapper">
        <div className="signup-form-container">
          <div className="flex justify-start mb-[50px]">
            <div className="w-[200px]">
              <img src="/logo-new.png" alt="Pinion" />
            </div>
          </div>
          {
            serverError
              ? <span className="text-app-base text-red-500 mb-app-xl">{serverError}</span>
              : null
          }
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col justify-center gap-app-xl ">
            <div className="flex flex-1 gap-app-base">
              <Input
                variant={InputVariants.OUTLINE}
                name={'First Name'}
                id={'first-name-id'}
                value={firstNameValue}
                errorMessage={'Letters, spaces, hyphens and apostrophes only'}
                hasError={firstNameError}
                onChange={onFirstNameChange}
                onBlur={onFirstNameBlur}
                required={true}
              />
              <Input
                variant={InputVariants.OUTLINE}
                name={'Last Name'}
                id={'last-name-id'}
                value={lastNameValue}
                errorMessage={'Letters, spaces, hyphens and apostrophes only'}
                hasError={lastNameError}
                onChange={onLastNameChange}
                onBlur={onLastNameBlur}
                required={true}
              />
            </div>
            <Input
              variant={InputVariants.OUTLINE}
              name={'email'}
              id={'emailid'}
              value={emailValue}
              errorMessage={'Invalid email'}
              hasError={emailError}
              onChange={onEmailChange}
              onBlur={onEmailBlur}
              required={true}
            />
            <div className="flex flex-col gap-app-xs">
              <Input
                variant={InputVariants.OUTLINE_PASSWORD}
                name={'password'}
                id={'passwordid'}
                value={passwordValue}
                errorMessage={'Password must be at least 8 characters'}
                hasError={passwordError}
                onChange={onPasswordChange}
                onBlur={onPasswordBlur}
                required={true}
              />
              {/* Proactive requirement hint; the red error replaces it once invalid. */}
              {!passwordError && (
                <span className="text-app-sm text-app-gray">At least 8 characters</span>
              )}
            </div>
            <Input
              variant={InputVariants.OUTLINE_PASSWORD}
              name={'confirm-password'}
              id={'confirm-passwordid'}
              value={confirmPasswordValue}
              errorMessage={'Passwords does not match'}
              hasError={confirmPasswordError}
              onChange={onConfirmPasswordChange}
              onBlur={onConfirmPasswordBlur}
              required={true}
            />

            <div className="flex flex-col gap-app-xs">
              <CheckBox
                variant={CheckBoxVariants.OUTLINE}
                id="terms-checkbox"
                handleCheckBoxChange={handleCheckBoxChange}
                label={'I accept the Terms of Service and Privacy Policy'}
              />
              {/* Real links to the documents. Kept out of the checkbox <label> so
                  that reading them doesn't toggle the box, and opened in a new
                  tab so signup (a modal) isn't lost. */}
              <span className="text-app-sm text-app-gray ml-[2.5rem]">
                Read the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-app-primary underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-app-primary underline">
                  Privacy Policy
                </Link>
                .
              </span>
            </div>

            <button
              className="flex p-app-base rounded-app-base justify-center bg-[#3742fa] hover:bg-[#2828dc] disabled:opacity-40 disabled:hover:bg-[#3742fa]"
              disabled={!isChecked}
            >
              Create account
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

